import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_USER_SERVICE } from '@/api/constant';
// import Breadcrumb from '@/components/common/Breadcrumb';
import Layout from '@/components/Layout/Layout';
import BtnLoader from '@/components/UI/loaders/BtnLoader';
import Card from '@/components/UI/Card';
import FormField from '@/components/UI/FormField';
import { IPaymentFormConfig, IPaymentFormConfigRes } from '@/interfaces/empDiscount.interface';
import ErrorMessage from '@/components/common/ErrorMessage';
import { showToastNotification } from '@/utility/helper';
import { errorTypes } from '@/utility/validation';
import React, { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { usePermissions } from '@/hooks/usePermissions';
import { getPermissionName } from '@/context/permission';

const AddEmployeeDiscount: React.FC = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    control,
  } = useForm<IPaymentFormConfig>({
    defaultValues: {
      minimumWithdrawalPoints: null,
      pointsPerCurrencyUnit: null,
      discountType: 'percentage',
      employeeDiscount: null,
      // taxRate: 0,
      deliveryFees: null,
      cod: null
    },
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [id, setId] = useState<string | undefined>('');
  const discountType = watch('discountType');
  const pointsPCU = useWatch({ control, name: 'pointsPerCurrencyUnit' });
  const { canEdit } = usePermissions()

  useEffect(() => {
    fetchDiscountInfo();
  }, []);

  const fetchDiscountInfo = async () => {
    try {
      const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.settings}`;
      const res = await callApi<IPaymentFormConfigRes[]>(PATH, { method: 'GET' });
      reset({
        deliveryFees: res?.[0]?.deliveryFees,
        discountType: res?.[0]?.discountType,
        employeeDiscount: res?.[0]?.employeeDiscount,
        minimumWithdrawalPoints: res?.[0]?.minimumWithdrawalPoints,
        pointsPerCurrencyUnit: res?.[0]?.pointsPerCurrencyUnit,
        cod: res?.[0]?.cod === true ? 'active' : 'inactive'
      });
      setId(res?.[0]?.id ? res?.[0]?.id : '');
    } catch (error) {
      console.log('error is', error);
    }
  };

  const onSubmit = async (data: IPaymentFormConfig) => {
    setIsLoading(true);
    try {
      const payload = {
        ...data,
        cod: data.cod === 'active' ? true : false
      }
      let PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.settings}${id ? `/${id}` : ''}`;
      await callApi(PATH, { method: id ? 'PUT' : 'POST', data: payload });
      setIsLoading(false);
      fetchDiscountInfo();
      showToastNotification('success', 'Data added successfully');
    } catch (error: unknown) {
      const err = error as { message?: string };
      setIsLoading(false);
      if (err.message) showToastNotification('danger', err.message);
      console.log('error in creating coupon', error);
    }
  };

  return (
    <Layout>
      {/* <Breadcrumb items={breadcrumb} /> */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <Card>
          <h2 className="text-lg font-semibold mb-3">Loyalty Points</h2>
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FormField
                  label="Min. Withdrawal Points"
                  type="text"
                  placeholder="Enter Min. Withdrawal Points"
                  {...register('minimumWithdrawalPoints', {
                    ...errorTypes.required('Min. Withdrawal Points'),
                    valueAsNumber: true,
                  })}
                  onChange={(e) => {
                    e.target.value = e.target.value
                      .replace(/[^0-9.]/g, '') // Allow only digits and dots
                      .replace(/(\..*)\./g, '$1'); // Prevent multiple dots
                  }}
                />
                <ErrorMessage
                  message={errors.minimumWithdrawalPoints?.message}
                />
              </div>
              <div>
                <FormField
                  label="Points Per Currency Unit"
                  type="text"
                  prefix="BD"
                  placeholder="Enter Points Per Currency Unit"
                  {...register('pointsPerCurrencyUnit', {
                    ...errorTypes.required('Min. Withdrawal Points'),
                    valueAsNumber: true,
                  })}
                />
                <ErrorMessage message={errors.pointsPerCurrencyUnit?.message} />
                <ErrorMessage
                  message={`${pointsPCU} points will be 1 BD`}
                  className="text-gray-300!"
                />
              </div>
            </div>
          </div>
        </Card>
        <Card>
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">Emp. Discount Value</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
              <div>
                <FormField
                  label="Discount Type"
                  type="select"
                  options={[
                    { value: 'percentage', label: 'Percentage' },
                    { value: 'flat', label: 'Fixed Amount' },
                  ]}
                  {...register('discountType', {
                    ...errorTypes.required('type'),
                  })}
                />
                <ErrorMessage message={errors.discountType?.message} />
              </div>
              <div>
                <FormField
                  label="Value"
                  type="text"
                  placeholder="20"
                  suffix={discountType === 'percentage' ? '%' : undefined}
                  prefix={discountType === 'flat' ? 'BD' : undefined}
                  {...register('employeeDiscount', {
                    valueAsNumber: true,
                    ...errorTypes.required('discount value'),
                  })}
                  onChange={(e) => {
                    e.target.value = e.target.value
                      .replace(/[^0-9.]/g, '') // Allow only digits and dots
                      .replace(/(\..*)\./g, '$1'); // Prevent multiple dots
                  }}
                />
                <ErrorMessage message={errors.employeeDiscount?.message} />
              </div>
            </div>
          </div>
        </Card>
        <Card>
          <h2 className="text-lg font-semibold mb-3">Global Order Settings</h2>
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* <div>
                <FormField
                  label="Tax Rate"
                  type="number"
                  min={0}
                  placeholder="Enter Tax Rate"
                  {...register('taxRate', {
                    ...errorTypes.required('Tax Rate'),
                  })}
                  suffix="%"
                />
                <ErrorMessage message={errors.taxRate?.message} />
              </div> */}
              <div>
                <FormField
                  label="Delivery fees"
                  type="text"
                  prefix="BD"
                  placeholder="Enter Delivery fees"
                  {...register('deliveryFees', {
                    ...errorTypes.required('Delivery fees'),
                    valueAsNumber: true,
                  })}
                  onChange={(e) => {
                    e.target.value = e.target.value
                      .replace(/[^0-9.]/g, '') // Allow only digits and dots
                      .replace(/(\..*)\./g, '$1'); // Prevent multiple dots
                  }}
                />
                <ErrorMessage message={errors.deliveryFees?.message} />
              </div>
              <div>
                <FormField
                  label="COD"
                  type="select"
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                  ]}
                  {...register('cod', {
                    ...errorTypes.required('Delivery '),
                  })}
                />
                <ErrorMessage message={errors.deliveryFees?.message} />
              </div>
            </div>
          </div>
        </Card>
        {
          canEdit(getPermissionName('update:settings')) && (
            <div className="flex justify-end gap-3">
              <button type="submit" className="btn-primary">
                {isLoading ? <BtnLoader /> : 'Save'}
              </button>
            </div>
          )
        }

      </form>
    </Layout>
  );
};

export default AddEmployeeDiscount;
